import { useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';

import type { CommerceState, LoyaltyState } from 'src/concierge/contract';

import useUser from 'hooks/use-user';
import useUI from 'hooks/use-ui';
import useCart from 'hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import { useConciergeDisplayName } from 'src/concierge/use-concierge-display-name';
import { useStoreStatus } from 'src/dispensary/core-menu/components/header/store-status/use-store-status';
import { useLoyalty } from 'src/components/widgets/loyalty-tiers/use-loyalty';
import type { useUserButton } from 'src/dispensary/core-menu/components/header/user-button/use-user-button';

import { mapDispensary, mapUser } from './utils';
import { useAccountMenu } from './use-account-menu';
import { useConciergeCart } from './use-concierge-cart';

type UserButton = ReturnType<typeof useUserButton>;

export function useCommerceState(userButton: UserButton): CommerceState {
  const User = useUser();
  const UI = useUI();
  const Cart = useCart();
  const { dispensary } = useDispensary();
  const conciergeName = useConciergeDisplayName();
  const { isOpen, openClosedText } = useStoreStatus({ dispensary });
  const accountMenu = useAccountMenu(userButton);
  const {
    loading: loyaltyLoading,
    isLoyaltyMember,
    loyaltyWidgetEnabled,
    rewardsProgramName,
    currentTierName,
    badgeColor,
    currentBalance,
    currentYearTierBalance,
    targetBalance,
    nextTierName,
    progressPercentageToNextTier,
    balanceDaysToExpiration,
    isPoints,
    isDiscountRedemption,
    isSingleTier,
    accrualRate,
  } = useLoyalty();

  const loyalty = useMemo<LoyaltyState>(() => {
    // Mirrors home widget: hide tier progression at the top tier or for single-tier programs.
    const hasNextTier = !isSingleTier && Boolean(nextTierName);
    const balanceToNext = Math.max(0, targetBalance - currentYearTierBalance);
    // `progressPercentageToNextTier` is a string like "94%"; LoyaltyCard wants a number.
    const progressPercent = Number.parseFloat(progressPercentageToNextTier) || 0;
    // Only structured perk in the schema is the accrual multiplier; free-text perks don't exist.
    const perk = accrualRate != null && accrualRate > 1 ? `${accrualRate}x points` : undefined;

    return {
      isMember: isLoyaltyMember,
      loading: loyaltyLoading,
      widgetEnabled: loyaltyWidgetEnabled ?? false,
      programName: rewardsProgramName,
      tierName: currentTierName,
      tierBadgeColor: badgeColor,
      balance: currentBalance,
      isPoints,
      isDiscountRedemption,
      daysToExpiration: balanceDaysToExpiration ?? null,
      next: hasNextTier && nextTierName ? { tier: nextTierName, balanceToNext, progressPercent, perk } : undefined,
    };
  }, [
    isLoyaltyMember,
    loyaltyLoading,
    loyaltyWidgetEnabled,
    rewardsProgramName,
    currentTierName,
    badgeColor,
    currentBalance,
    currentYearTierBalance,
    targetBalance,
    nextTierName,
    progressPercentageToNextTier,
    balanceDaysToExpiration,
    isPoints,
    isDiscountRedemption,
    isSingleTier,
    accrualRate,
  ]);

  const cart = useConciergeCart();
  // ConciergePage only mounts the concierge once isValidMenuVariant(UI.variant) passes, so the
  // variant is always embedded/kiosk/store-front here.
  const menuVariant = useObserver(() => UI.variant as CommerceState['menuVariant']);

  // Kiosk terminals are shared devices: identity crosses the bridge only for a
  // signed-in Kiosk Pro shopper. Regular kiosk and anonymous Pro stay userless.
  // UI.isKiosk follows the /kiosks/ route, so a kiosk route never passes a user even if
  // UI.variant ever resolves to another variant.
  const isKioskRoute = useObserver(() => UI.isKiosk);
  const isKioskSurface = isKioskRoute || menuVariant === 'kiosk';
  const isKioskProTerminal = useKioskPro();
  const isKioskPro = isKioskSurface ? isKioskProTerminal : undefined;
  const user = useObserver(() => {
    if (!User) return undefined;
    if (isKioskSurface && !(isKioskPro && User.isLoggedIn)) return undefined;
    return mapUser(User, accountMenu, loyalty);
  });

  const menuType = useObserver<CommerceState['menuType']>(
    () => (Cart?.menuType as CommerceState['menuType']) ?? undefined
  );
  const location = useMemo(() => mapDispensary(dispensary, { isOpen, openClosedText }), [
    dispensary,
    isOpen,
    openClosedText,
  ]);

  return useMemo(() => ({ cart, conciergeName, isKioskPro, location, menuType, menuVariant, user }), [
    cart,
    conciergeName,
    isKioskPro,
    location,
    menuType,
    menuVariant,
    user,
  ]);
}
